import React, { useEffect, useMemo, useState } from 'react';
import { localCalendarDateString } from '../../utils/localDate';
import { API_BASE_URL } from './orderFormHelpers';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { SearchableSelect } from '../ui/SearchableSelect';
import { Textarea } from '../ui/Textarea';
import { AlertMessage } from '../ui/AlertMessage';
import {
  HOLD_REASON_LABEL,
  VENDOR_LOSS_REASONS,
  LOSS_REASON_LABEL,
  PRODUCTION_LOSS_REASONS,
} from './workflowConstants';

async function uploadOrderDocument(orderId, file, documentType, authHeaders) {
  if (!orderId || !file) return null;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('orderId', String(orderId));
  formData.append('documentType', documentType);
  const res = await fetch(`${API_BASE_URL}/api/files/upload/order-document`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || err?.error || 'Upload failed');
  }
  const body = await res.json();
  return body.key || null;
}

// -------- Order-level HOLD modal
export function HoldModal({ open, onClose, onSubmit, busy }) {
  const [reason, setReason] = useState('AWAITING_PARTY_APPROVAL');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setReason('AWAITING_PARTY_APPROVAL');
      setNote('');
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Place order on hold"
      description="The order will stay locked until you release it."
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => onSubmit({ reason, note: note.trim() || undefined })} disabled={busy || !reason}>
            {busy ? 'Saving…' : 'Place on hold'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <Select
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        >
          {Object.entries(HOLD_REASON_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </Select>
        <Textarea
          label="Note (optional)"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Extra context for the shop floor…"
        />
      </div>
    </Modal>
  );
}

// -------- Generic confirm-with-note modal (approve / cancel / ready / release…)
export function ConfirmTransitionModal({ open, onClose, onSubmit, title, description, actionLabel = 'Confirm', actionVariant = 'primary', busy, requireReason }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) {
      setReason('');
      setNote('');
    }
  }, [open]);
  const canSubmit = !busy && (!requireReason || reason.trim().length > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant={actionVariant}
            onClick={() => onSubmit({ reason: reason.trim() || undefined, note: note.trim() || undefined })}
            disabled={!canSubmit}
          >
            {busy ? 'Working…' : actionLabel}
          </Button>
        </>
      )}
    >
      <div className="space-y-3">
        {requireReason && (
          <Input
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            placeholder="Why?"
          />
        )}
        <Textarea
          label="Note (optional)"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  );
}

// -------- Start beam — multi-beam: required machine; single-beam: required machine + pcs split
export function StartBeamModal({
  open,
  onClose,
  onSubmit,
  beam,
  busy,
  machineOptions = [],
  /** When true, machines must come from {@code machineOptions} (order has a production unit). */
  khataLinked = false,
  singleBeamDesign = false,
  plannedPieces = 0,
}) {
  const [machineNo, setMachineNo] = useState('');
  const [note, setNote] = useState('');
  const [splits, setSplits] = useState([{ machineNo: '', pieces: '' }]);
  const hasRoster = Array.isArray(machineOptions) && machineOptions.length > 0;
  const rosterEnforced = Boolean(khataLinked);
  const pickListOnly = rosterEnforced && hasRoster;
  const rosterBlocked = rosterEnforced && !hasRoster;

  const planned = Number(plannedPieces) || Number(beam?.plannedPieces) || 0;
  const useSplitMode = Boolean(singleBeamDesign && planned > 0);

  useEffect(() => {
    if (open) {
      setMachineNo(beam?.machineNo || '');
      setNote('');
      if (singleBeamDesign && planned > 0) {
        setSplits([{ machineNo: '', pieces: String(planned) }]);
      } else {
        setSplits([{ machineNo: '', pieces: '' }]);
      }
    }
  }, [open, beam, singleBeamDesign, planned]);

  const machineSelectOptions = useMemo(
    () =>
      (machineOptions || []).map((m) => ({
        value: String(m.machineNo || '').trim(),
        label: m.label ? `${m.machineNo} — ${m.label}` : String(m.machineNo || ''),
      })),
    [machineOptions]
  );

  const splitRowMachineOptions = useMemo(() => {
    const norm = (s) => String(s || '').trim().toLowerCase();
    return splits.map((_, idx) => {
      const taken = new Set(
        splits.map((r, i) => (i === idx ? '' : norm(r.machineNo))).filter(Boolean)
      );
      return machineSelectOptions.filter(
        (opt) => !taken.has(norm(opt.value)) || norm(opt.value) === norm(splits[idx].machineNo)
      );
    });
  }, [machineSelectOptions, splits]);

  const sumPieces = useMemo(
    () => splits.reduce((acc, row) => acc + (Number.parseInt(String(row.pieces ?? '').trim(), 10) || 0), 0),
    [splits]
  );

  const splitRowsValid = useMemo(() => {
    if (!useSplitMode) return true;
    return splits.every((row) => {
      const pcs = Number.parseInt(String(row.pieces ?? '').trim(), 10);
      return String(row.machineNo || '').trim().length > 0 && Number.isFinite(pcs) && pcs >= 1;
    });
  }, [splits, useSplitMode]);

  const splitSumOk = !useSplitMode || sumPieces === planned;
  const canSubmitSplits =
    useSplitMode && splitRowsValid && splitSumOk && splits.length >= 1 && !rosterBlocked;

  const addSplitRow = () => {
    setSplits((rows) => [...rows, { machineNo: '', pieces: '' }]);
  };

  const removeSplitRow = (idx) => {
    setSplits((rows) => (rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx)));
  };

  const setSplitRow = (idx, patch) => {
    setSplits((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const handleSubmit = () => {
    if (useSplitMode) {
      const machineSplits = splits
        .map((r) => ({
          machineNo: String(r.machineNo || '').trim(),
          pieces: Number.parseInt(String(r.pieces || '').trim(), 10),
        }))
        .filter((r) => r.machineNo && Number.isFinite(r.pieces) && r.pieces > 0);
      if (!canSubmitSplits || machineSplits.length === 0) return;
      onSubmit({ machineSplits, note: note.trim() || undefined });
      return;
    }
    if (!String(machineNo || '').trim()) return;
    onSubmit({ machineNo: machineNo.trim(), note: note.trim() || undefined });
  };

  const machineRequired = !useSplitMode;
  const machineOk = !machineRequired || Boolean(String(machineNo || '').trim());

  const submitDisabled =
    busy ||
    rosterBlocked ||
    (useSplitMode ? !canSubmitSplits : !machineOk);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={beam ? `Start beam #${beam.beamNo}` : 'Start beam'}
      description={
        rosterBlocked
          ? 'This order uses a production unit, but that unit has no machines yet. Add machines under Feeders & Colours → Production Units, then try again.'
          : useSplitMode
            ? `This single-beam run has ${planned.toLocaleString()} planned pieces. Assign each registered machine a share so the total matches exactly. You can run several machines in parallel.`
            : pickListOnly
              ? 'Select a machine from this order’s production unit.'
              : 'Enter the machine number that will run this beam.'
      }
      size={useSplitMode ? 'md' : 'sm'}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitDisabled}>
            {busy ? 'Starting…' : 'Start beam'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        {rosterBlocked ? (
          <AlertMessage
            type="warning"
            message="This order’s production unit has no machines yet. Add machines under Feeders & Colours → Production Units, then open Start beam again."
          />
        ) : null}
        {useSplitMode ? (
          <>
            <div className="flex flex-wrap items-end justify-between gap-2">
              <p className="text-sm font-semibold text-slate-800">Machines &amp; pieces</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addSplitRow}
                disabled={busy || splits.length >= 40 || rosterBlocked}
              >
                Add machine
              </Button>
            </div>
            <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-3">
              {splits.map((row, idx) => (
                <div key={idx} className="grid gap-2 sm:grid-cols-[1fr_6rem_auto] sm:items-end">
                  <div>
                    {pickListOnly ? (
                      <SearchableSelect
                        label={`Machine ${idx + 1}`}
                        placeholder="Type to find machine…"
                        emptyMessage="No machine starts with that text"
                        value={row.machineNo}
                        onChange={(id) => setSplitRow(idx, { machineNo: id })}
                        options={splitRowMachineOptions[idx] || []}
                      />
                    ) : rosterBlocked ? (
                      <div>
                        <p className="mb-1.5 text-sm font-medium text-gray-800">{`Machine ${idx + 1}`}</p>
                        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                          No machines registered for this production unit.
                        </p>
                      </div>
                    ) : (
                      <Input
                        label={`Machine ${idx + 1}`}
                        value={row.machineNo}
                        onChange={(e) => setSplitRow(idx, { machineNo: e.target.value })}
                        placeholder="e.g. M-01"
                      />
                    )}
                  </div>
                  <Input
                    label="PCS"
                    type="number"
                    min={1}
                    value={row.pieces}
                    onChange={(e) => setSplitRow(idx, { pieces: e.target.value })}
                  />
                  <div className="flex justify-end sm:pb-1">
                    {splits.length > 1 ? (
                      <button
                        type="button"
                        className="text-xs font-semibold text-rose-600 hover:underline"
                        onClick={() => removeSplitRow(idx)}
                        disabled={busy}
                      >
                        Remove
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400 sm:invisible"> </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div
              className={[
                'rounded-lg px-3 py-2 text-sm font-semibold tabular-nums',
                splitSumOk ? 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200' : 'bg-amber-50 text-amber-900 ring-1 ring-amber-200',
              ].join(' ')}
            >
              Assigned: {sumPieces.toLocaleString()} / {planned.toLocaleString()} planned
              {!splitSumOk ? <span className="ml-2 font-normal">— totals must match</span> : null}
            </div>
            {!splitRowsValid ? (
              <p className="text-xs text-rose-600">Each row needs a machine and at least 1 piece.</p>
            ) : null}
          </>
        ) : pickListOnly ? (
          <SearchableSelect
            label="Machine"
            required
            placeholder="Type to find machine…"
            emptyMessage="No machine starts with that text"
            value={machineNo}
            onChange={setMachineNo}
            options={machineSelectOptions}
          />
        ) : (
          <Input
            label="Machine no. *"
            value={machineNo}
            onChange={(e) => setMachineNo(e.target.value)}
            placeholder="e.g. M-01"
            required
          />
        )}
        {!useSplitMode && !machineOk ? (
          <p className="text-xs text-rose-600">Machine is required to start this beam.</p>
        ) : null}
        <Textarea
          label="Note (optional)"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  );
}

// -------- Complete beam (produced + scrapped counts)
export function CompleteBeamModal({ open, onClose, onSubmit, beam, busy }) {
  const planned = beam?.plannedPieces || 0;
  const planMissing = planned <= 0;
  const [produced, setProduced] = useState(0);
  const [scrapped, setScrapped] = useState(0);
  const [reason, setReason] = useState('THREAD_BREAK');
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setProduced(planMissing ? 0 : planned);
      setScrapped(0);
      setReason('THREAD_BREAK');
      setNote('');
      setError(null);
    }
  }, [open, planned, planMissing]);

  const total = (Number(produced) || 0) + (Number(scrapped) || 0);
  const overrun = !planMissing && total > planned;
  const short = !planMissing && total < planned;
  const mustMatch = !planMissing && !overrun && short;

  const submit = () => {
    if (overrun) {
      setError(`Produced + scrapped (${total}) cannot exceed planned (${planned}).`);
      return;
    }
    if (!planMissing && total < planned) {
      const gap = planned - total;
      setError(
        `Add the remaining ${gap} piece(s) to Scrapped (e.g. Shortfall) so good + scrapped = ${planned}.`
      );
      return;
    }
    if ((Number(produced) || 0) < 0 || (Number(scrapped) || 0) < 0) {
      setError('Counts cannot be negative.');
      return;
    }
    onSubmit({
      producedPieces: Number(produced) || 0,
      scrappedPieces: Number(scrapped) || 0,
      reason: (Number(scrapped) || 0) > 0 ? reason : undefined,
      note: note.trim() || undefined,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        beam
          ? Number(beam.machineSlot) > 1
            ? `Complete line ${beam.machineSlot} (beam #${beam.beamNo}${beam.machineNo ? ` · ${beam.machineNo}` : ''})`
            : `Complete beam #${beam.beamNo}`
          : 'Complete beam'
      }
      description={beam ? `${planned} piece(s) planned on this machine line.` : ''}
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="success" onClick={submit} disabled={busy || overrun || mustMatch}>
            {busy ? 'Saving…' : 'Mark beam done'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        {error && <AlertMessage type="error" message={error} />}
        {overrun && !error && (
          <AlertMessage type="error" message={`Produced + scrapped cannot exceed planned (${planned}).`} />
        )}
        {planMissing && (
          <AlertMessage type="info" message="Planned count was missing for this beam. Enter good and damaged pieces — they will be saved as the plan for this beam." />
        )}
        {short && !overrun && (
          <div className="space-y-2">
            <AlertMessage
              type="info"
              message={
                `Put the remaining ${planned - total} piece(s) in Scrapped if they were lost or not found — ` +
                'good + scrapped must add up to the planned count.'
              }
            />
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  setScrapped(Math.max(0, planned - (Number(produced) || 0)));
                  if (planned - (Number(produced) || 0) > 0) setReason('SHORTFALL');
                }}
              >
                Fill shortfall in Scrapped
              </Button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Produced (good)"
            type="number"
            min={0}
            max={planMissing ? undefined : planned}
            value={produced}
            onChange={(e) => {
              const raw = Number.parseInt(e.target.value, 10) || 0;
              const p = Math.max(0, raw);
              const capped = planMissing ? p : Math.min(p, planned);
              setProduced(capped);
              if (!planMissing) {
                setScrapped(Math.max(0, planned - capped));
              }
            }}
            required
          />
          <Input
            label="Scrapped (damaged)"
            type="number"
            min={0}
            max={planMissing ? undefined : planned}
            value={scrapped}
            onChange={(e) => setScrapped(Math.max(0, Number.parseInt(e.target.value, 10) || 0))}
          />
        </div>
        {Number(scrapped) > 0 && (
          <Select label="Scrap reason" value={reason} onChange={(e) => setReason(e.target.value)}>
            {PRODUCTION_LOSS_REASONS.map((r) => (
              <option key={r} value={r}>{LOSS_REASON_LABEL[r] || r}</option>
            ))}
          </Select>
        )}
        <Textarea
          label="Note (optional)"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  );
}

// -------- Beam hold / resume (simple note-only)
export function BeamHoldModal({ open, onClose, onSubmit, beam, mode = 'HOLD', busy }) {
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) setNote('');
  }, [open]);
  const isHold = mode === 'HOLD';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={beam ? `${isHold ? 'Pause' : 'Resume'} beam #${beam.beamNo}` : (isHold ? 'Pause beam' : 'Resume beam')}
      description={isHold ? 'The beam will be marked on hold until resumed.' : 'The beam will start running again.'}
      size="sm"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant={isHold ? 'secondary' : 'primary'}
            onClick={() => onSubmit({ note: note.trim() || undefined })}
            disabled={busy}
          >
            {busy ? 'Saving…' : isHold ? 'Pause beam' : 'Resume beam'}
          </Button>
        </>
      )}
    >
      <Textarea
        label="Note (optional)"
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={isHold ? 'e.g. thread break, waiting for supervisor…' : 'e.g. thread replaced, ready to resume…'}
      />
    </Modal>
  );
}

// -------- Send to vendor (order-level)
export function VendorSendModal({ open, onClose, onSubmit, stage, vendorParties, busy, defaults }) {
  const [vendorPartyId, setVendorPartyId] = useState('');
  const [chalanNo, setChalanNo] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setVendorPartyId('');
      setChalanNo(String(defaults?.chalanNo || '').trim());
      setNote('');
    }
  }, [open, defaults?.chalanNo]);

  const category = stage === 'BUTTA_CUTTING'
    ? 'BUTTA_CUTTING_PARTIES'
    : stage === 'MILL_FINISH'
      ? 'MILL_FINISH_PARTIES'
      : stage === 'BORDER_CUTTING'
        ? 'BORDER_CUTTING_PARTIES'
        : 'HANDWORK_PARTIES';
  const stageVendors = useMemo(
    () => (vendorParties || []).filter((p) => p.category === category),
    [vendorParties, category]
  );
  const title = stage === 'BUTTA_CUTTING'
    ? 'Send order to butta cutting'
    : stage === 'MILL_FINISH'
      ? 'Send order to mill finish'
      : stage === 'BORDER_CUTTING'
        ? 'Send order to border cutting'
        : 'Send order to handwork';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description="Hand the whole order over to a vendor for this stage."
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => onSubmit({
              stage,
              vendorPartyId: vendorPartyId.trim(),
              chalanNo: chalanNo.trim(),
              note: note.trim() || undefined,
            })}
            disabled={busy || !vendorPartyId || !chalanNo.trim()}
          >
            {busy ? 'Saving…' : 'Send to vendor'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <SearchableSelect
          label="Vendor"
          required
          value={vendorPartyId}
          placeholder={stageVendors.length ? 'Type to find vendor…' : 'No vendors yet'}
          emptyMessage="No vendor starts with that text"
          options={stageVendors.map((p) => ({ value: String(p.id), label: p.name || '' }))}
          onChange={setVendorPartyId}
        />
        {stageVendors.length === 0 && (
          <AlertMessage type="warning" message="No vendors configured for this category. Add one in Parties first." />
        )}
        <Input
          label="Chalan no. *"
          value={chalanNo}
          onChange={(e) => setChalanNo(e.target.value)}
          required
        />
        <Textarea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Modal>
  );
}

// -------- Receive from vendor (order-level, piecesLost + lossReason)
export function VendorReceiveModal({ open, onClose, onSubmit, stage, totalPieces, busy }) {
  const [piecesLost, setPiecesLost] = useState(0);
  const [reason, setReason] = useState('DAMAGED_AT_VENDOR');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setPiecesLost(0);
      setReason('DAMAGED_AT_VENDOR');
      setNote('');
    }
  }, [open]);

  const title = stage === 'BUTTA_CUTTING'
    ? 'Receive from butta cutting'
    : stage === 'MILL_FINISH'
      ? 'Receive from mill finish'
      : stage === 'BORDER_CUTTING'
        ? 'Receive from border cutting'
        : 'Receive from handwork';

  const lost = Number(piecesLost) || 0;
  const received = Math.max(0, (totalPieces || 0) - lost);
  const overrun = totalPieces != null && lost > totalPieces;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={totalPieces != null ? `${totalPieces} piece(s) were sent. Record how many came back.` : 'Record how many pieces came back.'}
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => onSubmit({
              stage,
              piecesLost: lost,
              lossReason: lost > 0 ? reason : undefined,
              note: note.trim() || undefined,
            })}
            disabled={busy || overrun}
          >
            {busy ? 'Saving…' : `Receive ${received} · ${lost} lost`}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        {overrun && (
          <AlertMessage type="error" message={`Pieces lost cannot exceed ${totalPieces}.`} />
        )}
        <Input
          label="Pieces lost / damaged"
          type="number"
          min={0}
          max={totalPieces ?? undefined}
          value={piecesLost}
          onChange={(e) => setPiecesLost(Math.max(0, Number.parseInt(e.target.value, 10) || 0))}
        />
        {lost > 0 && (
          <Select label="Loss reason" value={reason} onChange={(e) => setReason(e.target.value)}>
            {VENDOR_LOSS_REASONS.map((r) => (
              <option key={r} value={r}>{LOSS_REASON_LABEL[r] || r}</option>
            ))}
          </Select>
        )}
        <Textarea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Modal>
  );
}

// -------- Dispatch
export function DispatchModal({ open, onClose, onSubmit, defaults, busy }) {
  const [billNo, setBillNo] = useState('');
  const [chalanNo, setChalanNo] = useState('');
  const [dispatchDate, setDispatchDate] = useState('');
  const [price, setPrice] = useState('');
  const [damagedPieces, setDamagedPieces] = useState('0');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState('');
  const totalPieces = Number(defaults?.totalPieces) || 0;

  useEffect(() => {
    if (open) {
      setBillNo(defaults?.billNo || '');
      setChalanNo(defaults?.chalanNo || '');
      setDispatchDate(localCalendarDateString());
      setNote('');
      setPrice(defaults?.price != null && defaults?.price !== '' ? String(defaults.price) : '');
      const initialDamaged = defaults?.damagedPieces != null ? Number(defaults.damagedPieces) : 0;
      setDamagedPieces(String(Number.isFinite(initialDamaged) && initialDamaged >= 0 ? initialDamaged : 0));
      setFormError('');
    }
  }, [open, defaults]);

  const priceRaw = String(price).trim();
  const priceNum = priceRaw === '' ? null : Number.parseFloat(priceRaw);
  const priceInvalid = priceRaw !== '' && (!Number.isFinite(priceNum) || priceNum <= 0);
  const damagedNum = Number.parseInt(String(damagedPieces).trim(), 10);
  const damagedInvalid = !Number.isFinite(damagedNum) || damagedNum < 0
    || (totalPieces > 0 && damagedNum > totalPieces);
  const canSubmitDispatch = Boolean(dispatchDate && chalanNo.trim() && !damagedInvalid);

  const handleDispatch = () => {
    setFormError('');
    if (priceInvalid) {
      setFormError('Enter a valid price (greater than zero), or leave blank.');
      return;
    }
    if (!dispatchDate) {
      setFormError('Choose a dispatch date.');
      return;
    }
    if (!chalanNo.trim()) {
      setFormError('Chalan number is required.');
      return;
    }
    if (damagedInvalid) {
      setFormError(
        totalPieces > 0
          ? `Damaged pieces must be between 0 and ${totalPieces.toLocaleString()}.`
          : 'Damaged pieces cannot be negative.'
      );
      return;
    }
    onSubmit({
      billNo: billNo.trim() || undefined,
      chalanNo: chalanNo.trim(),
      dispatchDate,
      damagedPieces: damagedNum,
      ...(priceNum != null ? { price: priceNum } : {}),
      note: note.trim() || undefined,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Dispatch order"
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="success" onClick={handleDispatch} disabled={busy || !canSubmitDispatch}>
            {busy ? 'Saving…' : 'Dispatch order'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        {formError ? <AlertMessage type="error" message={formError} onClose={() => setFormError('')} /> : null}
        <Input
          label="Price"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        <Input label="Bill no." value={billNo} onChange={(e) => setBillNo(e.target.value)} />
        <Input label="Chalan no. *" value={chalanNo} onChange={(e) => setChalanNo(e.target.value)} required />
        <Input
          label="Damaged pcs"
          type="number"
          min={0}
          max={totalPieces > 0 ? totalPieces : undefined}
          value={damagedPieces}
          onChange={(e) => setDamagedPieces(e.target.value)}
          hint={
            defaults?.damagedFromProduction
              ? `Includes ${Number(defaults.damagedFromProduction).toLocaleString()} from production / vendor loss — adjust if needed.`
              : 'Enter 0 if none, or add damage found at dispatch.'
          }
        />
        <Input label="Dispatch date *" type="date" value={dispatchDate} onChange={(e) => setDispatchDate(e.target.value)} required />
        <Textarea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Modal>
  );
}

// -------- Deliver
export function DeliverModal({ open, onClose, onSubmit, defaults, busy, orderId }) {
  const [deliveryDate, setDeliveryDate] = useState('');
  const [note, setNote] = useState('');
  const [invoiceKey, setInvoiceKey] = useState('');
  const [lrKey, setLrKey] = useState('');
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [lrFile, setLrFile] = useState(null);
  const [localBusy, setLocalBusy] = useState(false);
  const [formError, setFormError] = useState('');

  const authHeaders = useMemo(() => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    const h = {};
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, []);

  useEffect(() => {
    if (open) {
      setDeliveryDate(localCalendarDateString());
      setNote('');
      setInvoiceKey(defaults?.dispatchInvoiceImage || '');
      setLrKey(defaults?.dispatchLrImage || '');
      setInvoiceFile(null);
      setLrFile(null);
      setFormError('');
      setLocalBusy(false);
    }
  }, [open, defaults]);

  const canSubmitDeliver = Boolean(orderId && deliveryDate);

  const handleDeliver = async () => {
    setFormError('');
    if (!deliveryDate) {
      setFormError('Choose a delivery date.');
      return;
    }
    setLocalBusy(true);
    try {
      let inv = invoiceKey.trim() || undefined;
      let lr = lrKey.trim() || undefined;
      if (invoiceFile) {
        inv = await uploadOrderDocument(orderId, invoiceFile, 'invoice', authHeaders);
      }
      if (lrFile) {
        lr = await uploadOrderDocument(orderId, lrFile, 'lr', authHeaders);
      }
      const payload = {
        deliveryDate,
        ...(inv ? { dispatchInvoiceImage: inv.trim() } : {}),
        ...(lr ? { dispatchLrImage: lr.trim() } : {}),
        note: note.trim() || undefined,
      };
      const ok = await onSubmit(payload);
      if (!ok) setFormError('Could not mark delivered. Fix any issues above and try again.');
    } catch (e) {
      setFormError(e?.message || 'Delivery failed');
    } finally {
      setLocalBusy(false);
    }
  };

  const blocking = busy || localBusy;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Mark order delivered"
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={blocking}>Cancel</Button>
          <Button variant="success" onClick={handleDeliver} disabled={blocking || !canSubmitDeliver}>
            {blocking ? 'Saving…' : 'Mark delivered'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        {formError ? <AlertMessage type="error" message={formError} onClose={() => setFormError('')} /> : null}
        <Input label="Delivery date *" type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} required />
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-800">Invoice image</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-primary-800"
            onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
          />
          {invoiceKey && !invoiceFile ? (
            <p className="mt-1 text-xs font-medium text-primary-700">Already on file — choose a new file to replace it.</p>
          ) : null}
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-800">LR image</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-primary-800"
            onChange={(e) => setLrFile(e.target.files?.[0] || null)}
          />
          {lrKey && !lrFile ? (
            <p className="mt-1 text-xs font-medium text-primary-700">Already on file — choose a new file to replace it.</p>
          ) : null}
        </div>
        <Textarea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Modal>
  );
}
